import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import { downloadCSV as downloadGenericCSV } from '../../lib/functions';
import Button from '../Button';
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const AlcoholTestingReport = ({ testData = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'test_date', label: 'Test Date' },
        { key: 'tested_person', label: 'Person Tested' },
        { key: 'bac_reading', label: 'BAC Reading' },
        { key: 'result', label: 'Result' },
        { key: 'administered_by', label: 'Administered By' },
        { key: 'comments', label: 'Comments' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true);
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
            } else {
                logger.log("Invalid password.");
                testData.length = 0;
                showAlert({
                    message: 'Invalid password. Access denied.',
                    type: 'danger',
                    buttons: [{ label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error validating password:", error);
            showAlert({
                message: 'An error occurred while validating the password.',
                type: 'danger',
                buttons: [{ label: 'Close' }],
            });
        }
        setOpenRequestPassword(false);
    };

    const showData = () => {
        logger.log("Alcohol test data received:", testData);
        
        if (!Array.isArray(testData) || testData.length === 0) {
            logger.log("No alcohol test data available");
            setTableData([]);
            setIsFiltered(true);
            return;
        }

        setIsFiltered(true);

        // Calculate summary statistics
        const total = testData.length;
        const failed = testData.filter(test => {
            const result = test.result || (parseFloat(test.bac_reading) >= 0.08 ? 'fail' : 'pass');
            return result === 'fail';
        }).length;
        const passed = total - failed;
        const passRate = total > 0 ? ((passed / total) * 100).toFixed(1) : 0;

        const stats = {
            total,
            passed,
            failed,
            passRate: passRate + '%'
        };
        setSummaryStats(stats);

        // Format filtered data
        const formatted = testData.map((test) => {
            const newEntry = {};
            displayColumns.forEach(({ key }) => {
                if (key === 'test_date') {
                    newEntry[key] = formatDate(test.test_date);
                } else if (key === 'tested_person') {
                    newEntry[key] = test.profiles ? test.profiles.name + ' ' + test.profiles.surname : 'Unknown';
                } else if (key === 'bac_reading') {
                    newEntry[key] = parseFloat(test.bac_reading).toFixed(3);
                } else if (key === 'result') {
                    newEntry[key] = test.result || (parseFloat(test.bac_reading) >= 0.08 ? 'fail' : 'pass');
                } else if (key === 'administered_by') {
                    newEntry[key] = test.created_by_profile ? test.created_by_profile.name + ' ' + test.created_by_profile.surname : 'Unknown';
                } else if (key === 'comments') {
                    newEntry[key] = test.comments || '—';
                } else {
                    newEntry[key] = test[key] ?? '—';
                }
            });
            return newEntry;
        });

        setTableData(formatted);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div>
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h3>Alcohol Testing Report</h3>
            </div>

            {/* Summary Statistics */}
            {isFiltered && Object.keys(summaryStats).length > 0 && (
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                    gap: '15px',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#17a2b8', margin: '0 0 5px 0' }}>{summaryStats.total}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Total Tests</p>
                    </div>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#28a745', margin: '0 0 5px 0' }}>{summaryStats.passed}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Passed</p>
                    </div>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#dc3545', margin: '0 0 5px 0' }}>{summaryStats.failed}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Failed</p>
                    </div>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#ffc107', margin: '0 0 5px 0' }}>{summaryStats.passRate}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Pass Rate</p>
                    </div>
                </div>
            )}

            {isFiltered && (
                <div style={{ 
                    maxHeight: '70vh', 
                    overflowY: 'auto',
                    border: `1px solid ${isLightTheme ? '#ddd' : '#555'}`,
                    borderRadius: '8px'
                }}>
                    <table className={`table table-striped table-hover mb-0 ${isLightTheme ? 'table-light' : 'table-dark'}`} style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse'
                    }}>
                        <thead className={isLightTheme ? 'table-dark' : 'table-light'}>
                            <tr style={{ 
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            }}>
                                {displayColumns.map(({ key, label }) => (
                                    <th
                                        key={key}
                                        className="text-center"
                                        style={{
                                            padding: '12px 8px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.length > 0 ? (
                                tableData.map((test, index) => (
                                    <tr key={index} style={{ 
                                        borderBottom: `1px solid ${isLightTheme ? '#ddd' : '#555'}`
                                    }}>
                                        {displayColumns.map(({ key }) => (
                                            <td
                                                key={key}
                                                className="text-center"
                                                style={{
                                                    padding: '10px 8px',
                                                    color: key === 'result' ? (
                                                        test[key] === 'pass' ? '#28a745' : 
                                                        test[key] === 'fail' ? '#dc3545' : 
                                                        'inherit'
                                                    ) : 'inherit',
                                                    fontWeight: key === 'result' ? 'bold' : 'normal'
                                                }}
                                            >
                                                {key === 'result' ? test[key].toUpperCase() : test[key]}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td 
                                        colSpan={displayColumns.length} 
                                        className="text-center"
                                        style={{ 
                                            padding: '30px',
                                            fontStyle: 'italic',
                                            color: isLightTheme ? '#666' : '#ccc'
                                        }}
                                    >
                                        No alcohol test data available for the selected date range.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AlcoholTestingReport;